<template>
  <div
      ref="containerEl"
      class="card-slider-container"
  >
    <div class="card-slider">
      <div class="container-fluid">
        <div class="card-slider-wrapper">
          <Carousel
              ref="carouselEl"
              v-if="slideItems.length"
              class="io-eq"
              :class="{ 'mobil': isMobileBreakpoint }"
              :items-to-show="itemsToShow"
              :transition="1025"
              :mouse-drag="allowDrag"
              :wrap-around="showNavigation"
              snap-align="start"
              @slide-start="triggerAutoEqualize"
          >
            <Component
                v-for="(slideItem, inx) in slideItems"
                :key="`slide-item-${inx}`"
                :is="slideItem"
            />
          </Carousel>
        </div>
      </div>
    </div>
    <template v-if="showNavigation && !allowDrag">
      <div
          class="card-slider__prev"
          :style="{
            color: `${props.branch?.customColor.primary}`,
          }"
          @click="toPrev"
      >
        <FontAwesomeIcon icon="fa-solid fa-chevrons-left"/>
      </div>
      <div
          class="card-slider__next"
          :style="{
            color: `${props.branch?.customColor.primary}`,
          }"
          @click="toNext"
      >
        <FontAwesomeIcon icon="fa-solid fa-chevrons-right"/>
      </div>
    </template>
  </div>
  <template v-if="showNavigation && allowDrag && props.arrowsActive">
    <div
        class="card-slider__prev-mobile"
        :style="{
            color: `${props.branch?.customColor.primary}`,
          }"
        @click="toPrev"
    >
      <FontAwesomeIcon icon="fa-solid fa-chevrons-left"/>
    </div>
    <div
        class="card-slider__next-mobile"
        :style="{
            color: `${props.branch?.customColor.primary}`,
          }"
        @click="toNext"
    >
      <FontAwesomeIcon icon="fa-solid fa-chevrons-right"/>
    </div>
  </template>
</template>

<script lang="ts" setup>
import Equalize from '@/utils/Equalize';
import {
  onBreakpointChanged,
  removeOnBreakpointChanged,
} from '@/utils/viewport';
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  Ref,
  ref,
  useSlots,
  h,
  VNode,
} from 'vue';
import {
  Carousel,
  Slide,
} from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faChevronsRight,
  faChevronsLeft,
} from '@fortawesome/pro-solid-svg-icons';
import BranchInterface from '@/interfaces/BranchInterface';

library.add(faChevronsRight, faChevronsLeft);

const carouselEl = ref<InstanceType<typeof Carousel>>();

const props = defineProps<{
  branch?: BranchInterface
  arrowsActive?: boolean
}>();

const slots = useSlots();
const slotDefault = computed(() => (slots.default ? slots.default() : null));
const slideItems = ref<VNode[]>([]);
const numItems = computed<number>(() => slideItems.value.length);

const toPrev = () => {
  if (!carouselEl.value) return;

  carouselEl.value.prev();
};

const toNext = () => {
  if (!carouselEl.value) return;

  carouselEl.value.next();
};

const isMobileBreakpoint = inject('isMobileBreakpoint') as Ref<boolean>;

const showNavigation = computed(() => (
  isMobileBreakpoint.value
    ? numItems.value > 1
    : numItems.value > 3
));
const itemsToShow = ref<number>(1);
const allowDrag = ref<boolean>(true);

const setItemsToShow = (event: CustomEvent) => {
  const breakpoints = event.detail?.breakpoints || [] as string[];
  const lastBreakpoint = [...(breakpoints || [])].reverse()[0] || 'xs';

  switch (lastBreakpoint) {
    case 'sm':
      itemsToShow.value = 2;
      allowDrag.value = true;
      break;
    case 'md':
      itemsToShow.value = 2;
      allowDrag.value = true;
      break;
    case 'lg':
      itemsToShow.value = 2;
      allowDrag.value = true;
      break;
    case 'xl':
      itemsToShow.value = 2;
      allowDrag.value = false;
      break;
    case 'xxl':
    case 'xxxl':
      itemsToShow.value = 3;
      allowDrag.value = false;
      break;
    default:
      itemsToShow.value = 1;
      allowDrag.value = true;
  }
};

const containerEl = ref<HTMLElement>();
const triggerAutoEqualize = () => {
  if (!containerEl.value) return;

  Equalize.run(containerEl.value);
};

const preventMapSlotItems = ref<boolean>(false);
const mapDefaultSlotItems = () => {
  if (preventMapSlotItems.value) {
    preventMapSlotItems.value = false;
    return;
  }
  let items: any[] = [];
  if (typeof slotDefault.value?.[0]?.type === 'symbol') {
    items = (slotDefault.value?.[0]?.children || []) as any[];
  } else {
    items = slotDefault.value || [];
  }

  preventMapSlotItems.value = true;
  nextTick(() => {
    slideItems.value = [...items].map((item: any) => h(Slide, {
      class: 'card-slider__item',
    }, () => [h('div', {
      class: 'card-slider__item-wrapper',
    }, [item])]));

    onBreakpointChanged(setItemsToShow, true);

    setTimeout(triggerAutoEqualize, 50);
  });
};

onMounted(mapDefaultSlotItems);

onUpdated(triggerAutoEqualize);
onUpdated(() => {
  mapDefaultSlotItems();
});

onBeforeUnmount(() => {
  removeOnBreakpointChanged(setItemsToShow);
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

body {
  overflow-x: hidden;
}

.card-slider {
  --mask-left-start: calc(max(var(--container-fluid-padding-left), (100vw - var(--container-fluid-max-width) - var(--container-fluid-padding-left) - var(--container-fluid-padding-right))) - 1px);
  --mask-left-end: calc(max(var(--container-fluid-padding-left), (100vw - var(--container-fluid-max-width) - var(--container-fluid-padding-left) - var(--container-fluid-padding-right))));
  --mask-width: 320px;
  --mask-image-content: linear-gradient(90deg, rgba(0,0,0, .5) 0%, black var(--mask-left-end), black calc(100% - var(--mask-left-end) - 20px), rgba(0,0,0, .5));
  --mask-size-content: 100% 100%;
  --mask-image-scrollbar: linear-gradient(black, black);
  --mask-size-scrollbar: 0 100%;
  mask-image: var(--mask-image-content), var(--mask-image-scrollbar);
  mask-size: var(--mask-size-content), var(--mask-size-scrollbar);
  mask-position: 0 0, 100% 0;
  mask-repeat: no-repeat, no-repeat;
  padding-bottom: 6px;

  @media (min-width: map_get($grid-breakpoints, 'xxl')) {
    --mask-image-content: linear-gradient(90deg, rgba(0,0,0, 0) 0%, rgba(0, 0, 0, 0) 20px, black var(--mask-left-end), black calc(100% - var(--mask-left-end) - 20px), transparent calc(100% - 20px), transparent);
  }

  .card-slider__item-wrapper {
    align-items: start;
    align-self: start;
    width: 100%;
    padding: 0 10px;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      padding: 0 15px;
    }

    > * {
      width: 100%;
      position: relative;
    }
  }

  &-container {
    position: relative;
    z-index: 2;
    overflow: hidden;
    width: 100vw;
    margin-left: calc(-1 * var(--container-fluid-padding-left));

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      margin-left: calc(-1 * (100vw - 100%) / 2);
    }

    .card-slider__prev,
    .card-slider__next {
      display: flex;
      align-items: center;
      position: absolute;
      justify-content: center;
      width: 5.125rem;
      height: 5.125rem;
      background: #FFFFFF;
      border-radius: 65px;
      color: #4BBECF;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
      font-size: 1.875rem;
      top: 162px;
      transform: translateY(-67%);
      cursor: pointer;

      @media (min-width: map_get($grid-breakpoints, 'sm')) {
        top: 192px;
      }

      @media (min-width: map_get($grid-breakpoints, 'md')) {
        top: 220px;
      }

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        top: 250px;
      }

      @media (min-width: 1600px) {
        top: 6.8125rem;
        transform: none;
      }
    }

    .card-slider__prev {
      left: 50px;
      transition: opacity .35s ease-in-out;
      margin: 0;

      &--disabled {
        opacity: 0;
      }
    }

    .card-slider__next {
      right: 50px;
      transition: opacity .35s ease-in-out;
      margin: 0;

      &--disabled {
        opacity: 0;
      }
    }
  }

  .carousel {
    transform: none;
    position: static;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      transform: translateX(-15px);
    }
  }

  .card-slider-wrapper {
    position: relative;
  }

  .carousel__viewport {
    overflow: visible;
  }

  .card-slider__card-wrapper {
    width: 100%;
    padding: 0 10px;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      padding: 0 15px;
    }
  }

  .card-slider__card {
    width: 100%;
  }
}

.card-slider__prev-mobile,
.card-slider__next-mobile {
  display: flex;
  align-items: center;
  position: absolute;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: #FFFFFF;
  border-radius: 65px;
  color: #000000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  font-size: 1.25rem;
  cursor: pointer;
}

.card-slider__prev-mobile {
  left: 65px;
  transition: opacity .35s ease-in-out;
  margin: 0;

  &--disabled {
    opacity: 0;
  }
}

.card-slider__next-mobile {
  right: 65px;
  transition: opacity .35s ease-in-out;
  margin: 0;

  &--disabled {
    opacity: 0;
  }
}
</style>
